import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "../lib/utils";

/**
 * StatusTimeline — Industrial Display Primitive (F3, TES-950)
 *
 * Vertikale Timeline mit before:-Schiene + Knoten current/done/upcoming
 * (Stitch screen-07:340-360). REINE PRAESENTATION — kein Status-Workflow-Editor,
 * keine defect_status-Logik. Aufrufer uebergibt fertige i18n-Strings.
 *
 *   - current  Knoten border-primary + innerer bg-primary-Punkt, Label fett text-primary
 *   - done     Knoten border-outline-variant + check-Icon
 *   - upcoming  Knoten leer, ganze Zeile opacity-50
 */
type TimelineItemState = "current" | "done" | "upcoming";

interface StatusTimelineItem {
  /** Stabiler Key. */
  id: string;
  /** Eyebrow-Zeile (Zeitstempel/Status-Eyebrow, i18n vom Aufrufer) — uppercase .text-label-caps. */
  timestamp: React.ReactNode;
  /** Haupt-Label (i18n vom Aufrufer). */
  label: React.ReactNode;
  /** Optionale Detailzeile (i18n vom Aufrufer). */
  detail?: React.ReactNode;
  state: TimelineItemState;
}

interface StatusTimelineProps extends React.HTMLAttributes<HTMLOListElement> {
  items: StatusTimelineItem[];
}

/** Knoten-Inhalt je Zustand (current: Punkt · done: check · upcoming: leer). */
function TimelineNodeMarker({ state }: { state: TimelineItemState }) {
  if (state === "current") {
    return <span className="w-2 h-2 bg-primary rounded-full" aria-hidden="true" />;
  }
  if (state === "done") {
    return <Check className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />;
  }
  return null;
}

// Zustands-abhaengige Klassen vorab (haelt die TimelineNode-Komplexitaet niedrig).
const NODE_LI_CLASS: Record<TimelineItemState, string> = {
  current: "",
  done: "",
  upcoming: "opacity-50",
};
const NODE_BORDER_CLASS: Record<TimelineItemState, string> = {
  current: "border-primary",
  done: "border-outline-variant",
  upcoming: "border-outline-variant",
};
const NODE_LABEL_CLASS: Record<TimelineItemState, string> = {
  current: "text-primary",
  done: "",
  upcoming: "",
};

function TimelineNode({ item }: { item: StatusTimelineItem }) {
  const { state, timestamp, label, detail } = item;
  return (
    <li className={cn("relative pl-8", NODE_LI_CLASS[state])}>
      <div
        className={cn(
          "absolute left-0 top-1 w-[24px] h-[24px] bg-background border-2 rounded-full flex items-center justify-center",
          NODE_BORDER_CLASS[state],
        )}
      >
        <TimelineNodeMarker state={state} />
      </div>
      <p className="text-label-caps text-muted-foreground">{timestamp}</p>
      <p className={cn("font-bold", NODE_LABEL_CLASS[state])}>{label}</p>
      {detail !== undefined && <p className="text-xs text-muted-foreground">{detail}</p>}
    </li>
  );
}

const StatusTimeline = React.forwardRef<HTMLOListElement, StatusTimelineProps>(
  ({ className, items, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        "relative space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-outline-variant",
        className,
      )}
      {...props}
    >
      {items.map((item) => (
        <TimelineNode key={item.id} item={item} />
      ))}
    </ol>
  ),
);
StatusTimeline.displayName = "StatusTimeline";

export { StatusTimeline };
